import React, { useEffect, useState, useMemo } from "react";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import { createAntdSelection } from "../../../../../../../../../utils/common";
import { fetchSubjectsAcademicView } from "../../../../../../../../../services/subject";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import { Alert, Col, Divider, Popconfirm, Row, Select, Space, Spin, Tabs } from "antd";
import { Controller, useForm } from "react-hook-form";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import { toast } from "react-toastify";
import styles from "./add-edit-schedule-activity.module.css";
import { ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import TabPane from "antd/es/tabs/TabPane";
import {
  createRoutine,
  editRoutine as EditRoutineApi,
  fetchRoutineByRoutineId,
  deleteRoutine,
} from "../../../../../../../../../services/class_routine";
import { getSubjectWiseTeachers } from "../../../../../../../../../services/teachers";
import SVGIcon from "../../../../../../../../../constants/SVGIcon";
import {
  fetchClass,
  fetchSections,
} from "../../../../../../../../../services/student";
import CustomTimePicker from "../../../../../../../../../common/input/custom-timepicker/custom-timepicker";
import dayjs from "dayjs";

const { Option } = Select;

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AddEditScheduleActivity = ({
  open,
  onClose,
  editDetails,
  selectedClass
}) => {
  const [loading, setLoading] = useState(false);
  const [levels, setLevels] = useState([]);
  const { control, trigger, reset, getValues, resetField, watch } = useForm({
    mode: "onChange",
    defaultValues: {
      routine_name: "",
      subject_logo: "",
      department: "",
      level: "",
      classname: "",
    },
  });
  const [isSchedulePublished, setIsSchedulePublished] = useState(true);
  const [activeDay, setActiveDay] = useState("Monday");
  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [subjectWiseTeachers, setSubjectWiseTeachers] = useState({});
  const formValues = watch();
  const [periods, setPeriods] = useState(
    Object.fromEntries(days.map((day) => [day, []]))
  );
  const titlePrefix = editDetails
    ? editDetails?.isReadOnly ? "" : "Edit "
    : "Add New ";

  const markRowChanged = (day, index) => {
    setPeriods((prev) => {
      const next = { ...prev };
      const arr = Array.isArray(next[day]) ? [...next[day]] : [];
      arr[index] = { ...(arr[index] || {}), changed: true };
      next[day] = arr;
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    if (!editDetails) return true;
    return days.some((d) => (periods[d] || []).some((p) => p?.changed));
  }, [periods, editDetails]);

  const fetchRoutineInfo = async () => {
    try {
      setLoading(true);
      const [subjectsResp, levels] = await Promise.all([
        fetchSubjectsAcademicView(),
        fetchClass(),
      ]);

      const subjects = subjectsResp.data?.data?.finalResult?.reduce(
        (acc, item) => {
          return acc.concat(createAntdSelection(item?.subjects));
        },
        []
      );

      setLevels(createAntdSelection(levels.data.data?.levels));
      setSubjects(subjects);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const buildDataForEditing = async (child) => {
    try {
      setLoading(true);
      const resp = await fetchRoutineByRoutineId(child?.routine_id);
      const data = resp?.data?.data?.schedulesByDay;
      const itemObj = Object.values(data)?.[0]?.[0];

      const obj = {};

      Object.entries(data).forEach((item) => {
        const [day, schedules] = item;

        obj[day] = schedules?.map((item) => {
          return {
            schedule_id: item?.id,
            subject: item?.subject_info?.[0]?.subject_id,
            teacher: item?.subject_info?.[0]?.teacher_id,
            startTime: dayjs(item?.start_time, "HH:mm:ss"),
            endTime: dayjs(item?.end_time, "HH:mm:ss"),
          };
        });
      });
      const constructData = {
        routine_name: itemObj?.routine_name,
        section_name: itemObj?.classroom_id,
        level: child.level_id,
        classname: child.classroom_id,
        routine_id: child?.routine_id,
        ...obj,
      };
      setIsSchedulePublished(resp?.data?.data?.isPublished);
      return constructData;
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    setActiveDay("Monday");
    (async () => {
      try {
        fetchRoutineInfo();
        fetchTeacherList();
        if (editDetails) {
          const editableRoutineData = await buildDataForEditing(editDetails);
          fetchClassroomBasedOnLevel(editDetails?.level_id);
          reset({ ...editableRoutineData });
          const obj = {};
          await Promise.all(
            days.map(async (i) => {
              obj[i] = Array.from(
                { length: editableRoutineData[i]?.length ?? 0 },
                () => ({ changed: false })
              );
            })
          );
          setPeriods(obj);
        } else {
          if (selectedClass?.level_id) {
            fetchClassroomBasedOnLevel(selectedClass?.level_id);
            reset({ 
              level: selectedClass?.level_id,
              classname: selectedClass?.classroom_id,
            });
          }
          setPeriods(Object.fromEntries(days.map((day) => [day, []])));
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [open, editDetails?.routine_id]);

  const fetchTeacherList = async () => {
    try {
      setLoading(true);
      let subjectWiseTeachersMap = {};
      const resp = await getSubjectWiseTeachers();

      if (resp?.data?.data) {
        resp?.data?.data?.forEach((item) => {
          subjectWiseTeachersMap[item.subject_id] = (item.teachers || []).map(
            (t) => ({
              value: t.teacher_id,
              label: t.teacher_name,
            })
          );
        });
      }
      setSubjectWiseTeachers(subjectWiseTeachersMap);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPeriod = () => {
    setPeriods((prev) => ({
      ...prev,
      [activeDay]: [...prev[activeDay], { changed: true }],
    }));
  };

  const handleRemovePeriod = async (day, index) => {
    try {
      setLoading(true);
      const getValue = getValues();
      const item = getValue?.[day] || [];
      const target = item[index] || {};
    
      if (editDetails && target?.schedule_id) {
        await deleteRoutine(target.schedule_id);
      }
      
      const newRoutine = item?.filter((_, idx) => idx !== index);
      const buildObject = { ...getValue, [day]: newRoutine };
      reset(buildObject);
      setPeriods((prev) => ({
        ...prev,
        [day]: prev[day].filter((_, i) => i !== index),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || "Failed to delete period");
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = async (day, index, subjectId, onChange) => {
    onChange(subjectId);

    const currentValues = getValues();
    if (currentValues?.[day]?.[index]?.teacher) {
      const updatedDay = [...(currentValues[day] || [])];
      updatedDay[index] = {
        ...updatedDay[index],
        teacher: null,
      };
      reset({
        ...currentValues,
        [day]: updatedDay,
      });
    }
    markRowChanged(day, index);
  };

  const renderPeriodInputs = (day) =>
    periods[day]?.map((_, index) => (
      <div className={styles.periodRow} key={index}>
        <Row gutter={[12, 12]} style={{ width: "100%" }}>
          <Col span={6} className={styles.tabInput}>
            <label className={styles.label_styles}>
              Subject<span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name={`${day}.${index}.subject`}
              control={control}
              rules={{ required: "Subject is required" }}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  placeholder="Select Subject"
                  size="large"
                  status={error ? "error" : ""}
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    return option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  showSearch={true}
                  onChange={(val) =>
                    handleSubjectChange(day, index, val, field.onChange)
                  }
                  disabled={!!editDetails?.isReadOnly}
                >
                  {subjects.map((subject) => (
                    <Option key={subject.value} value={subject.value}>
                      {subject.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </Col>

          <Col span={6} className={styles.tabInput}>
            <label className={styles.label_styles}>
              Teacher<span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name={`${day}.${index}.teacher`}
              control={control}
              rules={{ required: "Teacher is required" }}
              render={({ field, fieldState: { error } }) => {
                const selectedSubject = formValues?.[day]?.[index]?.subject;
                const teachers = subjectWiseTeachers[selectedSubject] || [];
                const exists = teachers?.some(t => t?.value === field?.value);
                return (
                  <Select
                    {...field}
                    value={exists ? field.value : null}
                    placeholder="Select Teacher"
                    size="large"
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      return option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    status={error ? "error" : ""}
                    showSearch
                    disabled={!selectedSubject || !!editDetails?.isReadOnly}
                    onChange={(val) => {
                      field.onChange(val);
                      markRowChanged(day, index);
                    }}
                  >
                    {teachers?.map((teacher) => (
                      <Option key={teacher.value} value={teacher.value}>
                        {teacher.label}
                      </Option>
                    ))}
                  </Select>
                );
              }}
            />
          </Col>

          <Col span={6} className={styles.tabInput}>
            <label className={styles.label_styles}>
              Start Time<span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name={`${day}.${index}.startTime`}
              control={control}
              rules={{ required: "Start time is required" }}
              render={({ field, fieldState: { error } }) => (
                <CustomTimePicker
                  {...field}
                  placeholder="Start Time"
                  size="large"
                  format="hh:mm a"
                  style={{ width: "100%", height: 40 }}
                  status={error ? "error" : ""}
                  suffixIcon={
                    <ClockCircleOutlined
                      style={{ color: "#999999", fontSize: 20 }}
                    />
                  }
                  onChange={(val) => {
                    field.onChange(val);
                    markRowChanged(day, index);
                  }}
                  disabled={!!editDetails?.isReadOnly}
                />
              )}
            />
          </Col>

          <Col span={6} className={styles.tabInput}>
            <label className={styles.label_styles}>
              End Time<span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name={`${day}.${index}.endTime`}
              control={control}
              rules={{ required: "End time is required" }}
              render={({ field, fieldState: { error } }) => (
                <CustomTimePicker
                  {...field}
                  placeholder="End Time"
                  size="large"
                  format="hh:mm a"
                  style={{ width: "100%", height: 40 }}
                  status={error ? "error" : ""}
                  suffixIcon={
                    <ClockCircleOutlined
                      style={{ color: "#999999", fontSize: 20 }}
                    />
                  }
                  onChange={(val) => {
                    field.onChange(val);
                    markRowChanged(day, index);
                  }}
                  disabled={!!editDetails?.isReadOnly}
                />
              )}
            />
          </Col>
        </Row>

        {!editDetails?.isReadOnly ? (
          <div className={styles.deleteIconContainer}>
            <SVGIcon
              name={"delete"}
              width={"20"}
              height={"20"}
              stroke={"rgba(243, 17, 17, 1)"}
              onClick={() => handleRemovePeriod(day, index)}
            />
          </div>
        ) : null}
      </div>
    ));

  const onSubmit = async () => {
    try {
      const value = getValues();
      const isValid = await trigger();
      const isScheduleExist = Object.keys(value).some((i) => days.includes(i));
      if (isValid && isScheduleExist) {
        setLoading(true);
        let schedules = [];

        days.forEach((day) => {
          const routine = value[day] || [];
          const daySchedules = routine?.map((period, idx) => {
            const meta = periods?.[day]?.[idx];
            if (editDetails && !meta?.changed) return null;
            const scheduleData = {
              classroom_id: value.classname,
              subject_info: [
                {
                  subject_id: period?.subject,
                  teacher_id: period?.teacher,
                },
              ],
              start_time: dayjs(period?.startTime).format("HH:mm:ss"),
              end_time: dayjs(period?.endTime).format("HH:mm:ss"),
              day,
            };
            if (editDetails && period?.schedule_id) {
              scheduleData.schedule_id = period?.schedule_id;
            }
            return scheduleData;
          });
          schedules = schedules.concat(daySchedules?.filter(Boolean) ?? []);
        });

        const payload = {
          routine_name: value.routine_name,
          schedules,
        };

        if (!isSchedulePublished) {
          payload.isPublished = true;
        } else if (editDetails && (schedules?.length ?? 0) === 0) {
          setLoading(false);
          toast.info("No changes detected to save.");
          return;
        }

        const values = getValues();
        if (editDetails) await EditRoutineApi(values?.routine_id, payload);
        else await createRoutine(payload);
        handleClose();
        toast.success(`Routine is ${editDetails ? "Edited" : "Created"} successfully!`);
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Failed to add routine");
    } finally {
      setLoading(false);
    }
  };

  const fetchClassroomBasedOnLevel = async (level_id) => {
    try {
      setLoading(true);
      resetField("classname", { defaultValue: "" });
      const resp = await fetchSections(level_id);
      const data = resp?.data?.data?.classrooms;
      setClassrooms(createAntdSelection(data));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const dayFields = Object.fromEntries(days?.map((d) => [d, []]));
    setPeriods(dayFields);
    setIsSchedulePublished(true);
    reset({
      routine_name: "",
      subject_logo: "",
      department: "",
      level: "",
      classname: "",
    });
    onClose();
  };

  const renderActionButton = () => {
    if (editDetails?.isReadOnly) return null;

    if (isSchedulePublished) {
      return (
        <CustomButton
          title={`${editDetails ? "Edit" : "Add "} Schedule`}
          size="large"
          onClick={onSubmit}
          disabled={!hasChanges}
        />
      );
    }

    return (
      <Popconfirm
        title={`Confirm publishing the schedule with the existing changes!`}
        onConfirm={onSubmit}
        okText="Yes"
        cancelText="No"
      >
        <div>
          <CustomButton 
            title="Publish Schedule" 
            size="large" 
            className={styles.popConfirmPublishButton} 
          />
        </div>
      </Popconfirm>
    );
  };

  return (
    <CustomDrawer
      title={`${titlePrefix}Schedule & Activity`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          {renderActionButton()}
        </div>
      }
      width="65vw"
      drawerStyles={{ body: { padding: "20px" } }}
    >
      <Spin spinning={loading}>
        <Row style={{ rowGap: "10px" }} gutter={20}>
          <Col span={8}>
            <Controller
              name="routine_name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="routine_name"
                  label="Schedule & Activity Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter Schedule & Activity Name*"
                  size="large"
                  disabled={!!editDetails?.isReadOnly}
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={8}>
            <Controller
              name="level"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id="level"
                  label="Level"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  disabled={!!editDetails}
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                    fetchClassroomBasedOnLevel(val);
                  }}
                  status={error ? "error" : ""}
                >
                  {levels?.map((option, index) => (
                    <Select.Option
                      key={`${option?.value}-${index}`}
                      value={option?.value}
                    >
                      {option?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
          <Col span={8}>
            <Controller
              name="classname"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id="classname"
                  label="Class Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  value={value}
                  disabled={!!editDetails}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {classrooms?.map((option, index) => (
                    <Select.Option
                      key={`${option?.value}-${index}`}
                      value={option?.value}
                    >
                      {option?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
        </Row>
        <Divider style={{ border: "1px solid rgba(5, 5, 5, 0.06)" }} />

        {!isSchedulePublished && !editDetails?.isReadOnly ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <>
                <strong>Schedule is not published.</strong>{" "}
                <span style={{ fontSize: 13, color: "#595959" }}>
                  Students can view it only after publishing.
                </span>
              </>
            }
          />
        ) : null}

        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Tabs
            defaultActiveKey="Monday"
            activeKey={activeDay}
            onChange={(key) => setActiveDay(key)}
            tabBarExtraContent={{
              right: !editDetails?.isReadOnly && (
                <CustomButton
                  title="Add Period"
                  type="text"
                  onClick={handleAddPeriod}
                  icon={
                    <div className={styles.iconCircle}>
                      <PlusOutlined />
                    </div>
                  }
                  className={styles.addSubjectBtn}
                  showTooltip={false}
                />
              ),
            }}
          >
            {days.map((day) => (
              <TabPane tab={day} key={day} forceRender>
                {renderPeriodInputs(day)}
              </TabPane>
            ))}
          </Tabs>
        </Space>
      </Spin>
    </CustomDrawer>
  );
};

export default AddEditScheduleActivity;
